import React, {Component, CSSProperties, FormEvent, KeyboardEvent} from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import {Col, FormControl, InputGroup} from "react-bootstrap"
import {isFunction, omit} from "lodash"

import {ValidationInput} from "@irati/utils/validation"
import {ValidationProps, ValidationState} from "@irati/utils/validation/Input"

import {injectIntl, getResolvedMessage} from "@irati/utils/lang"
import onEnter from "@irati/utils/onEnter"
import newId from "@irati/utils/id"

import ErrorText from "../ErrorText"
import {FormGroup} from "../Form"
import {IconTypes} from "../Icon"
import {Label as LabelRegular} from "../Typography"
import mainTheme from "../global/theme"
import IconText from "../IconText"
import CharacterCountDown from "./CharacterCountDown"
import InfoTooltip from "./utils/InfoTooltip"

import ClearIconButton from "./styled/ClearIconButton"
import Label from "./styled/Label"
import {InputGroupAddon, StyledIconText} from "./styled"

const INPUT_TYPE_TEXT = "text"

export type OnInputChange = (value: string) => void

export interface InputProps extends ValidationProps {
  label: string
  name: string
  className?: string
  id?: string
  value?: string
  onChange?: OnInputChange
  onBlur?: (value: string) => void
  onEnter?: (e: FormEvent) => void
  type?: string
  hint?: string
  addon?: any
  clearable?: boolean
  style?: CSSProperties
  error?: string
  validValueText?: string
  maxLength?: number
  includeCharCountdown?: boolean
  tooltip?: string
  tooltipPosition?: string
  customTooltip?: boolean
  hideBorder?: boolean
  flush?: boolean
  hideLabel?: boolean
  placeholder?: string
  addonBackground?: string
  min?: string
  max?: string
  useWarningStyling?: boolean
  inputRef?: (input: HTMLInputElement | null) => void
}

interface State extends ValidationState {
  blurred: boolean
}

export class FormItemInput extends Component<InputProps, State> {
  validation: ValidationInput
  input: HTMLInputElement

  static contextTypes = {
    currentScreen: PropTypes.string
  }

  static defaultProps: Omit<InputProps, "intl" | "formatMessage" | "label" | "name"> = {
    type: INPUT_TYPE_TEXT,
    hideLabel: false,
    addon: {},
    clearable: false,
    includeCharCountdown: true,
    customTooltip: false,
    flush: false
  }

  constructor(props: InputProps) {
    super(props)
    this.state = {
      blurred: false,
      value: props.value
    }

    this.validation = new ValidationInput(this)
  }

  componentDidUpdate(prevProps: InputProps, prevState: State) {
    if (this.state.blurred) {
      this.validation.update(prevProps, prevState)
    }
  }

  componentWillUnmount() {
    this.validation.destroy()
  }

  UNSAFE_componentWillReceiveProps(nextProps: InputProps) {
    this.setState({value: nextProps.value})
  }

  blur = () => {
    if (isFunction(this.input.blur)) this.input.blur()
  }

  handleBlur = () => {
    this.setState({blurred: true})
    this.validation.update()
    if (this.props.onBlur) {
      this.props.onBlur(this.state.value)
    }
  }

  handleChange = (e: FormEvent<FormControl>) => {
    let {value} = e.target as HTMLInputElement
    if (value === "") {
      value = null
    }
    this.setState({value})
    if (this.props.onChange) {
      this.props.onChange(value)
    }
  }

  handleEnter = (e: KeyboardEvent<FormControl>) => {
    if (this.props.onEnter) {
      onEnter(e, () => {
        this.props.onEnter!(e)
      })
    }
  }

  resetBlur = () => {
    this.setState({
      blurred: false,
      errorMsg: undefined
    })
  }

  render() {
    const {
      id,
      name,
      label,
      type,
      hint,
      hideLabel,
      addon,
      clearable,
      style,
      error,
      validValueText,
      maxLength,
      includeCharCountdown,
      tooltip,
      tooltipPosition,
      customTooltip,
      hideBorder,
      flush,
      addonBackground,
      useWarningStyling
    } = this.props
    const {errorMsg} = this.state
    const className = classNames("perkboxInput", this.props.className)
    let {value} = this.state

    if (!value) {
      // if null/undefined => use ""
      value = ""
    }

    const inputProps = omit(this.props, [
      "id",
      "className",
      "label",
      "hint",
      "validation",
      "hideLabel",
      "onEnter",
      "clearable",
      "addon",
      "formatMessage",
      "translate",
      "includeCharCountdown",
      "customTooltip",
      "flush"
    ])
    const isClearable = value && clearable && type && type.toLowerCase() === INPUT_TYPE_TEXT

    const errorExists = error || errorMsg
    const showValidValueText = validValueText && !!value && !errorExists

    const inputStyling: CSSProperties = {
      ...style,
      borderColor: mainTheme.colors.greys.grey20,
      zIndex: "unset",
      minHeight: 48
    }
    if (addon.before || addon.after) {
      inputStyling.marginTop = 0
    }

    const noBorderStyling: CSSProperties = {border: "none", boxShadow: "none", paddingLeft: 0}
    const inputStyle: CSSProperties = hideBorder ? {...inputStyling, ...noBorderStyling} : inputStyling

    if (hint) inputStyle.marginBottom = 0
    if (isClearable) inputStyle.paddingRight = 28

    const input = (
      <FormControl
        inputRef={(c) => {
          this.input = c
        }}
        {...inputProps}
        placeholder={getResolvedMessage(this, "placeholder")}
        style={inputStyle}
        type={type}
        min={this.props.min}
        max={this.props.max}
        value={value}
        onBlur={this.handleBlur}
        onChange={this.handleChange}
        onKeyPress={this.handleEnter}
      />
    )

    return (
      <FormGroup
        className={className}
        controlId={id || name}
        validationState={errorMsg ? "error" : undefined}
        flush={flush}
        addonBackground={addonBackground}>
        <Col hidden={hideLabel} xs={tooltip ? 10 : 12}>
          {label && (
            <Label id={newId("label", id)} error={errorMsg} htmlFor={name} flush>
              {getResolvedMessage(this, "label")}
            </Label>
          )}
        </Col>
        {tooltip && (
          <Col xs={2}>
            <InfoTooltip tooltip={tooltip} position={tooltipPosition} customIcon={customTooltip} />
          </Col>
        )}
        <Col xs={12}>
          {addon.before || addon.after ? (
            <InputGroupAddon>
              {addon.before && <InputGroup.Addon>{addon.before}</InputGroup.Addon>}
              {input}
              {addon.after && <InputGroup.Addon>{addon.after}</InputGroup.Addon>}
            </InputGroupAddon>
          ) : (
            input
          )}
          {isClearable && <ClearIconButton id={newId("btn-clear", id)} size="10" icon={IconTypes.cross} onClick={this.handleChange} />}
          {errorExists && (
            <ErrorText id={newId("copy-error", id)}>
              {getResolvedMessage(this, "errorMsg", this.state) || getResolvedMessage(this, "error")}
            </ErrorText>
          )}
          {showValidValueText && <LabelRegular>{validValueText}</LabelRegular>}
          {hint && (
            <div>
              {useWarningStyling ? (
                <StyledIconText
                  icon={IconTypes.circleAlertFill}
                  id={newId("copy-hint", id)}
                  small
                  marginTop={0.5}
                  color={mainTheme.colors.utility.warning}>
                  {hint}
                </StyledIconText>
              ) : (
                <IconText icon={IconTypes.info} id={newId("copy-hint", id)} small marginTop={0.5} color={mainTheme.colors.greys.grey50}>
                  {hint}
                </IconText>
              )}
            </div>
          )}
          {maxLength && includeCharCountdown && (
            <div>
              <CharacterCountDown maxLength={maxLength} value={value} />
            </div>
          )}
        </Col>
      </FormGroup>
    )
  }
}

export default injectIntl(FormItemInput, {withRef: true})
